import { findBaseEntryConveyor } from '@sim/core/base';
import { isGateSatisfied } from '@sim/core/gate-condition';
import type { MachineKind } from '@sim/core/entities';
import { findEntityAt } from '@sim/core/grid';
import { hasUnwiredInput, isObservableConnectedToBase, machinePorts } from '@sim/core/routing';
import { observableCap } from '@sim/core/observable';
import type { SimState } from '@sim/core/state';
import { danglingConveyorTarget } from '@sim/core/subscriber';
import type { GridPos, GridRect } from '@sim/core/types';
import { translate } from '@sim/core/types';
import {
  COAL_SEAM_EXPANSION_ID,
  IRON_RIDGE_EXPANSION_ID,
  STAGE1_SINK_POS,
} from '@sim/content/stage1-layout';
import { unlockGateFor } from '@sim/content/unlock-gates';

export interface MilestoneContext {
  state: SimState;
  hasLeakedBefore: boolean;
  hasUnsubscribedOnce: boolean;
  hasSlagReachedSubscriber: boolean;
  hasSubscriptionErrored: boolean;
}

export type SingleMilestoneAnchor =
  | { kind: 'dom'; selector: string }
  | { kind: 'grid'; pos: (ctx: MilestoneContext) => GridPos | null }
  | { kind: 'gridRect'; rect: (ctx: MilestoneContext) => GridRect | null };

export type MilestoneAnchor =
  | SingleMilestoneAnchor
  | { kind: 'none' }
  /** Highlights several regions at once — e.g. the toolbar button *and* its drop target for a drag gesture that spans both. The coach-mark bubble positions itself against `anchors[0]`. */
  | { kind: 'multi'; anchors: readonly SingleMilestoneAnchor[] };

export interface MilestoneDef {
  id: string;
  title: string;
  body: string;
  isTriggered: (ctx: MilestoneContext) => boolean;
  anchor: MilestoneAnchor;
  /**
   * When set, this milestone auto-dismisses itself once true, instead of waiting for the player to
   * click "Got it" — used to chain scripted steps (e.g. "pick this tool" -> "now place it").
   */
  autoCompleteWhen?: (ctx: MilestoneContext) => boolean;
  /**
   * Only set this when `autoCompleteWhen` depends on ephemeral, non-persisted state (e.g. the
   * currently selected tool) that resets on reload — it lets `isMilestoneComplete` treat the step
   * as done once a *later* step in its group is already satisfied, since that later step could only
   * have been reached by passing through this one. Steps gated on saved game state (an observable
   * exists, a pipe connects, a subscription is on) must NOT set this: their own condition being
   * false always means genuinely not done, even if some later, unrelated condition is already true
   * (e.g. subscribing before the pipe is actually wired up).
   */
  ephemeral?: boolean;
  /**
   * Set to `false` for a step whose whole point is unrestricted map access (e.g. dragging pipes
   * across many cells) — the coach-mark still points at `anchor` for the tooltip, but skips the
   * darkened backdrop and its click/hover lockout, which would otherwise cover everything outside
   * the anchor's single cell. Defaults to `true`.
   */
  spotlight?: boolean;
  /** Shown as an external reference link on a splash-style ({ anchor: { kind: 'none' } }) milestone. */
  link?: { label: string; url: string };
  /**
   * The independent intensity axis (see BACKLOG.md's v1 design guidelines): `walkthrough`
   * (blocking spotlight, sim paused, map locked — reserved for the first-time-mechanics arc
   * `welcome` -> `operator-hallmark` only), `notice` (lightweight toast, non-blocking —
   * everything unlocked after that), or `achievement` (lightweight, own track/log).
   */
  intensity: 'walkthrough' | 'notice' | 'achievement';
}

function domAnchor(selector: string): SingleMilestoneAnchor {
  return { kind: 'dom', selector };
}

function sourceAnchor(): SingleMilestoneAnchor {
  return { kind: 'grid', pos: ({ state }) => state.sources[0]?.position ?? null };
}

function observableAnchor(): SingleMilestoneAnchor {
  return {
    kind: 'grid',
    pos: ({ state }) => Object.values(state.observables)[0]?.position ?? null,
  };
}

/** The interval still waiting on a period — never {@link observableAnchor}, which always resolves to the player's first (`of`) observable. */
function unconfiguredIntervalAnchor(): SingleMilestoneAnchor {
  return {
    kind: 'grid',
    pos: ({ state }) =>
      Object.values(state.observables).find(
        (observable) => observable.kind === 'interval' && observable.periodTicks === null,
      )?.position ?? null,
  };
}

/** The join of this kind still missing a stream on one of its inputs — the one the "wire both sides" step is about. */
function unwiredJoinAnchor(kind: MachineKind): SingleMilestoneAnchor {
  return {
    kind: 'grid',
    pos: ({ state }) =>
      Object.values(state.machines).find(
        (machine) => machine.kind === kind && hasUnwiredInput(state, machine),
      )?.position ?? null,
  };
}

/** The source an expansion just brought in — expansions append, so the newest is always the last. */
function newestSourceAnchor(): SingleMilestoneAnchor {
  return {
    kind: 'grid',
    pos: ({ state }) => state.sources[state.sources.length - 1]?.position ?? null,
  };
}

/**
 * The straight run of cells from the observable to the sink — the sink itself doesn't exist as an
 * entity yet at this point in the setup arc (it's only created once the Subscriber is placed), so
 * this reads the sink's fixed map position directly rather than `state.sinks`, same as `sourceAnchor`
 * already reads `state.sources` instead of a placed entity. Highlights the whole path as one strip
 * rather than just the endpoints, so the player sees exactly how far and which way to drag —
 * without needing to already know what a "Subscriber" is.
 */
function layPipePathAnchor(): SingleMilestoneAnchor {
  return {
    kind: 'gridRect',
    rect: ({ state }) => {
      const observable = Object.values(state.observables)[0];
      if (!observable) return null;
      const minX = Math.min(observable.position.x, STAGE1_SINK_POS.x);
      const maxX = Math.max(observable.position.x, STAGE1_SINK_POS.x);
      return {
        min: { x: minX, y: observable.position.y },
        max: { x: maxX, y: observable.position.y },
      };
    },
  };
}

/**
 * The bounding rect of every cell the pipe path touches — observable, conveyors, and any operators
 * between them — stopping *before* the subscriber entry conveyor so the spotlight never bleeds
 * into the subscriber itself.
 */
function pipePathAnchor(): SingleMilestoneAnchor {
  return {
    kind: 'gridRect',
    rect: ({ state }) => {
      const observable = Object.values(state.observables)[0];
      if (!observable) return null;
      const entry = findBaseEntryConveyor(state);
      if (!entry) return null;
      const { outSide } = machinePorts(state, observable.position);
      if (!outSide) return null;

      const positions: GridPos[] = [observable.position];
      let pos = observable.position;
      let dir = outSide;
      const visited = new Set<string>();
      for (let guard = 0; guard < 1000; guard++) {
        const nextPos = translate(pos, dir);
        const target = findEntityAt(state, nextPos);
        if (!target) break;

        const key = `${target.kind}:${target.id}`;
        if (visited.has(key)) break;
        visited.add(key);

        if (target.kind === 'conveyor') {
          const conveyor = state.conveyors[target.id];
          positions.push(conveyor.position);
          if (entry && target.id === entry.id) break;
          pos = conveyor.position;
          dir = conveyor.direction;
        } else if (target.kind === 'machine') {
          const machine = state.machines[target.id];
          positions.push(machine.position);
          const ports = machinePorts(state, machine.position);
          if (!ports.outSide) break;
          pos = machine.position;
          dir = ports.outSide;
        } else {
          break;
        }
      }

      if (positions.length === 0) return null;
      const xs = positions.map((p) => p.x);
      const ys = positions.map((p) => p.y);
      return {
        min: { x: Math.min(...xs), y: Math.min(...ys) },
        max: { x: Math.max(...xs), y: Math.max(...ys) },
      };
    },
  };
}

/** Highlights the toolbar button a drag starts from together with the map cell it must land on. */
function multiAnchor(...anchors: readonly SingleMilestoneAnchor[]): MilestoneAnchor {
  return { kind: 'multi', anchors };
}

function subscriberAnchor(): MilestoneAnchor {
  return { kind: 'gridRect', rect: ({ state }) => state.base };
}

/** The pre-placement "set up subscription" CTA cell — the empty cell a dangling pipe points into. */
function subscriptionCtaAnchor(): MilestoneAnchor {
  return { kind: 'grid', pos: ({ state }) => danglingConveyorTarget(state) };
}

/** The post-placement "plug in" / "pull plug" CTA cell — the conveyor feeding the base. */
function entryConveyorAnchor(): MilestoneAnchor {
  return { kind: 'grid', pos: ({ state }) => findBaseEntryConveyor(state)?.position ?? null };
}

const cashReadoutAnchor = domAnchor('[data-coachmark="cash-readout"]');

export function isObservableConnectedToSubscriber(state: SimState): boolean {
  const observable = Object.values(state.observables)[0];
  if (!observable) return false;
  return isObservableConnectedToBase(state, observable);
}

/**
 * True only once an observable has stopped producing AND every packet it already emitted has finished
 * its trip through the subscriber — not the instant the observable runs dry, since packets still in
 * flight will keep landing sales right up until they arrive.
 */
export function hasFullyDrainedObservable(state: SimState): boolean {
  return (
    state.subscribed &&
    Object.values(state.observables).some(
      (observable) => observable.cursor >= observableCap(observable),
    ) &&
    Object.keys(state.packets).length === 0
  );
}

export const MILESTONES: readonly MilestoneDef[] = [
  {
    id: 'welcome',
    title: 'Welcome',
    body: "You're running an automated extraction rig — packets of raw material will flow through it exactly the way values flow through an RxJS stream.",
    isTriggered: () => true,
    anchor: { kind: 'none' },
    intensity: 'walkthrough',
  },
  {
    id: 'place-observable',
    title: 'Place your observable',
    body: 'Drag `of()` from the Observables section onto the marked source to build your observable there.',
    isTriggered: () => true,
    anchor: multiAnchor(domAnchor('[data-coachmark="tool-of"]'), sourceAnchor()),
    autoCompleteWhen: ({ state }) => Object.keys(state.observables).length > 0,
    intensity: 'walkthrough',
  },
  {
    id: 'configure-observable',
    title: 'Buy its first slot',
    body: 'Click the + next to your observable to buy its first slot — each one costs more than the last.',
    isTriggered: () => true,
    anchor: observableAnchor(),
    autoCompleteWhen: ({ state }) =>
      Object.values(state.observables).some((observable) => observable.slotCosts.length > 0),
    intensity: 'walkthrough',
  },
  {
    id: 'lay-pipe',
    title: 'Lay some pipe',
    body: 'Drag from your observable, one cell at a time, to lay pipe along the highlighted path.',
    isTriggered: () => true,
    anchor: layPipePathAnchor(),
    autoCompleteWhen: ({ state }) => Object.keys(state.conveyors).length > 0,
    intensity: 'walkthrough',
  },
  {
    id: 'place-subscriber',
    title: 'Plug it in',
    body: 'Lay a pipe out from your observable, then click "set up subscription" at its far end.',
    isTriggered: () => true,
    anchor: subscriptionCtaAnchor(),
    autoCompleteWhen: ({ state }) => state.base !== null,
    spotlight: false,
    intensity: 'walkthrough',
  },
  {
    id: 'add-sell-function',
    title: 'Give it something to do',
    body:
      'Click the + and pick TreasuryService.deposit() — a Subscriber is where a stream finally ' +
      'causes something, and this one books whatever the pipe paid out into your balance.',
    isTriggered: ({ state }) => state.base !== null,
    anchor: subscriberAnchor(),
    autoCompleteWhen: ({ state }) => (state.subscriber?.functions.next.length ?? 0) > 0,
    intensity: 'walkthrough',
  },
  {
    id: 'subscription-errored',
    title: 'Your subscription errored',
    body:
      'A packet reached a sell method that was not written to handle it, so the method threw. An ' +
      'unhandled error is terminal in RxJS — it tears the whole subscription down, and nothing ' +
      'flows again until you fix the cause. Either send that `switchMap()` only the material its ' +
      'method accepts, or `filter()` the rest out upstream of it.',
    isTriggered: ({ hasSubscriptionErrored }) => hasSubscriptionErrored,
    anchor: { kind: 'none' },
    link: { label: 'Error handling in RxJS', url: 'https://rxjs.dev/guide/observable' },
    intensity: 'walkthrough',
  },
  {
    id: 'handle-subscription-error',
    title: 'At least ask it why',
    body:
      'A Subscriber has three columns and only `next` is open. Drag one of its walls outward to ' +
      'reveal `error`, then install AuditService.report() there. It will not save the stream — an ' +
      'error that reaches the Subscriber is already terminal — but it is the difference between a ' +
      'board that dies in silence and one that tells you what killed it.',
    isTriggered: ({ hasSubscriptionErrored }) => hasSubscriptionErrored,
    anchor: subscriberAnchor(),
    autoCompleteWhen: ({ state }) => (state.subscriber?.functions.error.length ?? 0) > 0,
    spotlight: false,
    intensity: 'notice',
  },
  {
    id: 'connect-observable-to-subscriber',
    title: 'Wire it up',
    body: 'A full pipe path has to connect your observable to the Subscriber before anything can flow.',
    isTriggered: () => true,
    anchor: observableAnchor(),
    autoCompleteWhen: ({ state }) => isObservableConnectedToSubscriber(state),
    spotlight: false,
    intensity: 'walkthrough',
  },
  {
    id: 'place-selling-operator',
    title: 'Nothing sells itself',
    body:
      'deposit() books proceeds — but nothing upstream has made any yet. Drop a `switchMap()` onto ' +
      'your pipe and give it a MarketService sell method: the market is only reachable from inside ' +
      'a stream, so goods go in and proceeds come out on the way past.',
    isTriggered: ({ state }) => (state.subscriber?.functions.next.length ?? 0) > 0,
    // Anchored, never a bare splash: this step is only satisfiable by dragging the tile out of the
    // toolbar, and a walkthrough with no anchor renders as a blocking splash over the very map the
    // player has to reach.
    anchor: multiAnchor(domAnchor('[data-coachmark="tool-switchMap"]'), pipePathAnchor()),
    autoCompleteWhen: ({ state }) =>
      Object.values(state.machines).some((machine) => machine.kind === 'switchMap'),
    spotlight: false,
    intensity: 'walkthrough',
  },
  {
    id: 'observable-subscribed',
    title: 'Subscribe it',
    body: 'Click "sub" to `.subscribe()` — nothing flows until you do.',
    isTriggered: () => true,
    anchor: entryConveyorAnchor(),
    autoCompleteWhen: ({ state }) => state.subscribed,
    intensity: 'walkthrough',
  },
  {
    id: 'flowing',
    title: "It's working!",
    body: 'Packets are moving now — watch a few make the trip from observable to Subscriber.',
    isTriggered: () => true,
    anchor: observableAnchor(),
    spotlight: false,
    intensity: 'walkthrough',
  },
  {
    id: 'first-cash',
    title: 'First sale',
    body: 'Sold! The pipe turned goods into proceeds, and deposit() booked them into your balance up top.',
    isTriggered: ({ state }) => state.economy.saleCount > 0,
    anchor: cashReadoutAnchor,
    intensity: 'walkthrough',
  },
  {
    id: 'observable-exhausted',
    title: 'Batch complete',
    body: "Your observable just emitted its last packet — but it's still subscribed, and upkeep never stops. Watch the balance.",
    isTriggered: ({ state }) => hasFullyDrainedObservable(state),
    anchor: cashReadoutAnchor,
    intensity: 'walkthrough',
  },
  {
    id: 'force-unsubscribe',
    title: 'Unsubscribe it',
    body: 'Click "unsub" to unsubscribe and stop the drain.',
    isTriggered: ({ state }) => hasFullyDrainedObservable(state),
    anchor: entryConveyorAnchor(),
    autoCompleteWhen: ({ state, hasLeakedBefore }) =>
      hasLeakedBefore && !hasFullyDrainedObservable(state),
    intensity: 'walkthrough',
  },
  {
    id: 'unsubscribe-hallmark',
    title: 'Nicely done — you just unsubscribed',
    body: 'Calling `unsubscribe()` tears down a subscription and frees whatever it was holding onto — skipping it is exactly how memory leaks happen in real apps. You can subscribe to the same observable again any time; it replays the same sequence from the start.',
    isTriggered: ({ hasUnsubscribedOnce }) => hasUnsubscribedOnce,
    anchor: { kind: 'none' },
    link: { label: 'Why unsubscribing matters', url: 'https://rxjs.dev/guide/subscription' },
    intensity: 'walkthrough',
  },
  {
    id: 'map-unlocked',
    title: 'Try an operator',
    body: 'You can afford `map()` now — drag it from Operators onto an existing pipe to slot it in.',
    isTriggered: ({ state }) => isGateSatisfied(state, unlockGateFor('map')),
    anchor: multiAnchor(domAnchor('[data-coachmark="tool-map"]'), pipePathAnchor()),
    autoCompleteWhen: ({ state }) => Object.values(state.machines).some((m) => m.kind === 'map'),
    intensity: 'walkthrough',
  },
  {
    id: 'operator-hallmark',
    title: 'Your first operator',
    body: '`map()` is your first RxJS operator — code that sits between an observable and a subscriber, transforming each packet as it passes through. Every operator you unlock works the same way: drop it onto a pipe, and it runs on every packet that flows past.',
    isTriggered: ({ state }) => Object.values(state.machines).some((m) => m.kind === 'map'),
    anchor: { kind: 'none' },
    intensity: 'walkthrough',
  },
  {
    id: 'take-unlocked',
    title: 'Auto-complete',
    body: '`take()` unsubscribes itself after N packets — cleanup with no manual `.unsubscribe()`.',
    isTriggered: ({ state }) => isGateSatisfied(state, unlockGateFor('take')),
    anchor: domAnchor('[data-coachmark="tool-take"]'),
    intensity: 'notice',
  },
  {
    id: 'world-expanded',
    title: 'The map just grew',
    body: 'New ground opened up to the south-east, and there is an iron seam on it. Iron sells well — but this source comes with slag mixed in.',
    isTriggered: ({ state }) => state.appliedExpansions.includes(IRON_RIDGE_EXPANSION_ID),
    anchor: newestSourceAnchor(),
    intensity: 'notice',
  },
  {
    id: 'slag-reaches-subscriber',
    title: 'That one was worthless',
    body: 'Slag sells for less than nothing — it just cost you cash. `filter()` can drop it before it ever reaches the `switchMap()` that sells it.',
    isTriggered: ({ hasSlagReachedSubscriber }) => hasSlagReachedSubscriber,
    anchor: cashReadoutAnchor,
    intensity: 'notice',
  },
  {
    id: 'filter-unlocked',
    title: 'Another operator',
    body: "You can afford `filter()` now — drag it from Operators onto an existing pipe to drop packets that don't match.",
    isTriggered: ({ state }) => isGateSatisfied(state, unlockGateFor('filter')),
    anchor: multiAnchor(domAnchor('[data-coachmark="tool-filter"]'), pipePathAnchor()),
    autoCompleteWhen: ({ state }) => Object.values(state.machines).some((m) => m.kind === 'filter'),
    intensity: 'notice',
  },
  {
    id: 'filter-placed',
    title: 'Filter it out',
    body: "`filter()` drops anything that doesn't match, instead of letting it flow through.",
    isTriggered: ({ state }) => Object.values(state.machines).some((m) => m.kind === 'filter'),
    anchor: domAnchor('[data-coachmark="tool-filter"]'),
    intensity: 'notice',
  },
  {
    id: 'interval-unlocked',
    title: 'A stream that never ends',
    body: '`interval()` emits forever on a timer — no last packet, no automatic teardown. Drag it onto a source, pick a period, and upkeep runs until `take()` or you stop it.',
    isTriggered: ({ state }) => isGateSatisfied(state, unlockGateFor('interval')),
    anchor: domAnchor('[data-coachmark="tool-interval"]'),
    autoCompleteWhen: ({ state }) =>
      Object.values(state.observables).some((observable) => observable.kind === 'interval'),
    intensity: 'notice',
  },
  {
    id: 'interval-placed',
    title: 'Give it a period',
    body: 'Pick how often it fires. A shorter period pays out faster but costs more up front — the stream is endless either way.',
    isTriggered: ({ state }) =>
      Object.values(state.observables).some((observable) => observable.kind === 'interval'),
    anchor: unconfiguredIntervalAnchor(),
    autoCompleteWhen: ({ state }) =>
      Object.values(state.observables).some(
        (observable) => observable.kind === 'interval' && observable.periodTicks !== null,
      ),
    intensity: 'notice',
  },
  {
    id: 'coal-seam-arrived',
    title: 'A second seam',
    body: 'More ground, and a coal seam on it — pure carbon, nothing to filter out. Run it alongside your iron and both streams have to share one pipe, arriving in whatever order they happen to turn up.',
    isTriggered: ({ state }) => state.appliedExpansions.includes(COAL_SEAM_EXPANSION_ID),
    anchor: newestSourceAnchor(),
    intensity: 'notice',
  },
  {
    id: 'zip-unlocked',
    title: 'Pair them up',
    body: '`zip()` waits for a packet on each of its two inputs, then packs the two into one packet. It has no arguments to set — the streams you wire into its sides are its arguments. Drop it on a pipe and run a second pipe into its free side.',
    isTriggered: ({ state }) => isGateSatisfied(state, unlockGateFor('zip')),
    anchor: multiAnchor(domAnchor('[data-coachmark="tool-zip"]'), pipePathAnchor()),
    autoCompleteWhen: ({ state }) => Object.values(state.machines).some((m) => m.kind === 'zip'),
    intensity: 'notice',
  },
  {
    id: 'zip-placed',
    title: 'It needs both halves',
    body: 'Until a packet is waiting on each input, `zip()` holds what it has and the pipe backs up behind it. What comes out is a pair, not a product — `zip()` transforms nothing and never looks at what it packs. Put a `map()` behind it with a recipe that takes the pair, or the `switchMap()` downstream is handed a pair no sell method will quote and the subscription errors out.',
    isTriggered: ({ state }) => Object.values(state.machines).some((m) => m.kind === 'zip'),
    anchor: unwiredJoinAnchor('zip'),
    autoCompleteWhen: ({ state }) =>
      Object.values(state.machines).some(
        (machine) => machine.kind === 'zip' && !hasUnwiredInput(state, machine),
      ),
    intensity: 'notice',
  },
  {
    id: 'combine-latest-unlocked',
    title: 'The other way to join',
    body: '`combineLatest()` looks like `zip()` and does the opposite: it fires every time *either* input delivers, pairing that packet with whatever the other input delivered last — and it spends neither. Nothing is consumed, so nothing has to arrive in step.',
    isTriggered: ({ state }) => isGateSatisfied(state, unlockGateFor('combineLatest')),
    anchor: multiAnchor(domAnchor('[data-coachmark="tool-combineLatest"]'), pipePathAnchor()),
    autoCompleteWhen: ({ state }) =>
      Object.values(state.machines).some((m) => m.kind === 'combineLatest'),
    intensity: 'notice',
  },
  {
    id: 'combine-latest-placed',
    title: 'It never spends what it remembers',
    body: 'Wire both sides and watch what it costs you: one carbon arriving once turns every iron after it into steel, out of nothing. That is why it is the wrong tool for a recipe — `zip()` spends both halves, this spends neither. Its honest use is an input that *should* keep being reused.',
    isTriggered: ({ state }) =>
      Object.values(state.machines).some((m) => m.kind === 'combineLatest'),
    anchor: unwiredJoinAnchor('combineLatest'),
    autoCompleteWhen: ({ state }) =>
      Object.values(state.machines).some(
        (machine) => machine.kind === 'combineLatest' && !hasUnwiredInput(state, machine),
      ),
    intensity: 'notice',
  },
];

/**
 * Scripted steps whose instructions only make sense in order (e.g. "land it on an observable" is
 * meaningless if the observable tool was never picked). None of a group's steps are persisted as seen
 * until its last id — the hallmark — completes, so a mid-group reload restarts the whole group
 * from its first step rather than stranding the player on a later step whose precondition (often
 * transient UI state, not saved game state) no longer holds.
 */
export const SETUP_GROUP = [
  'place-observable',
  'configure-observable',
  'lay-pipe',
  'place-subscriber',
  'add-sell-function',
  'connect-observable-to-subscriber',
  'place-selling-operator',
  'observable-subscribed',
] as const;

/** The sim clock stays frozen (see `SimClockService`) until this milestone is fully complete. */
export const SETUP_HALLMARK_ID = SETUP_GROUP[SETUP_GROUP.length - 1];

export const FILTER_INTRO_GROUP = ['filter-unlocked', 'filter-placed'] as const;

export const INTERVAL_INTRO_GROUP = ['interval-unlocked', 'interval-placed'] as const;

export const ZIP_INTRO_GROUP = ['zip-unlocked', 'zip-placed'] as const;

export const COMBINE_LATEST_INTRO_GROUP = [
  'combine-latest-unlocked',
  'combine-latest-placed',
] as const;

export const MILESTONE_GROUPS: readonly (readonly string[])[] = [
  SETUP_GROUP,
  FILTER_INTRO_GROUP,
  INTERVAL_INTRO_GROUP,
  ZIP_INTRO_GROUP,
  COMBINE_LATEST_INTRO_GROUP,
];

export function groupContaining(id: string): readonly string[] | null {
  return MILESTONE_GROUPS.find((group) => group.includes(id)) ?? null;
}

export function isGroupHallmark(id: string): boolean {
  const group = groupContaining(id);
  return group !== null && group[group.length - 1] === id;
}

/**
 * A scripted step waits for the action it describes rather than for a click, so it offers no way out
 * — that is what keeps the setup arc in order. A `notice` never gets that power even when it
 * auto-completes: what it suggests is optional, and a lightweight toast that cannot be closed is a nag.
 */
export function isMilestoneDismissible(milestone: MilestoneDef): boolean {
  return !milestone.autoCompleteWhen || milestone.intensity === 'notice';
}

/**
 * True once a milestone's own `autoCompleteWhen` is satisfied, OR — for an `ephemeral` grouped
 * step only — once a *later, non-ephemeral* step in the same group is satisfied (see `ephemeral`'s
 * doc comment for why non-ephemeral steps must never be skipped this way). A later *ephemeral*
 * step doesn't count: two ephemeral steps typically each gate on a different, mutually exclusive
 * tool selection, so one being satisfied says nothing about whether this one ever was.
 */
export function isMilestoneComplete(milestone: MilestoneDef, ctx: MilestoneContext): boolean {
  if (milestone.autoCompleteWhen?.(ctx)) return true;
  if (!milestone.ephemeral) return false;
  const group = groupContaining(milestone.id);
  if (!group) return false;
  return group.slice(group.indexOf(milestone.id) + 1).some((laterId) => {
    const later = MILESTONES.find((m) => m.id === laterId);
    return !later?.ephemeral && (later?.autoCompleteWhen?.(ctx) ?? false);
  });
}
